import React, { useState, useMemo, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
} from 'recharts';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
// ChartHelpButton removed for preset view (no title icon)
import { getCleanOptions, getSmartYDomain, formatDateShort, getSelectedFieldValue, pickBestTerminalCombination, sizeReader, readSizeGroup } from '../../utils/analyticsHelpers';
import { commoditySizeRules } from './pulse/index.js';
import { toggleMultiValue, shouldNameSeriesForMulti, applyMultiOrAllFilter, packagePinActive } from '../../../utils/filterLabels';
import { multiSelectDropdownLabel } from './terminalPricePulseTableUtils';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { useSyncedChartScroll } from './useSyncedChartScroll';
import {
  getLineChartHorizontalScrollDimensions,
  getChartScrollYAxisGutterPx,
  CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN,
  CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE,
} from './chartHorizontalScrollLayout';
import { getChartYAxisWidth, getYAxisDomainLabels } from './chartStyleUtils';
import usePersistWidgetSnapshot from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import { LinePriceTooltipBody } from './ChartTooltipContent';
import { pulseSkuFromItem, pushPulseSku, pickPackageWithMostRecentMonthPoints, defaultPulseVarieties, VARIETY_SELECT_MAX } from './pulseSkuTooltip';
import { comboSuffix, pulseLineColor } from './priceLineSeries';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import { TerminalPricePulseScorecards } from '../analytics/KpiScorecards';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip, PresetChartLegend, presetChartBodyClass } from '../analytics/PresetChartChrome';
import { useIsBelowLg, useIsPhone, getResponsiveChartMargin } from '../../hooks/useMediaQuery';
import { singleSelectionLabel } from '../../../utils/filterLabels';

const Y_AXIS_TICK_COUNT = 6;
const LEGEND_HEIGHT_PX = 48;
const PULSE_CHART_MARGIN_Y_DEFAULT = 20;
const PULSE_GUTTER_MARGIN_Y_MIN = 2;
const PULSE_GUTTER_MARGIN_Y_MAX = 120;

function formatPriceTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$${Number(n.toFixed(2))}`;
}

/** Vertical SVG line: true if x is (nearly) constant (column / Y-axis spine / vertical grid). */
function isVerticalSvgLine(line) {
  if (!line || line.tagName !== 'line') return false;
  const x1 = parseFloat(line.getAttribute('x1') || '0');
  const x2 = parseFloat(line.getAttribute('x2') || '0');
  return Number.isFinite(x1) && Number.isFinite(x2) && Math.abs(x1 - x2) < 0.5;
}

function verticalLineSpanPx(line) {
  if (!line) return null;
  const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
  const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
  if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;
  return Math.abs(y2 - y1);
}

/** Restrict rows by selected origins and/or terminals (empty array = no filter on that axis). */
const applyGeographicDataFilter = (rows, origins, locations) => {
  let filtered = rows;
  if (origins?.length > 0) {
    filtered = filtered.filter((item) => item.origin && origins.includes(item.origin));
  }
  if (locations?.length > 0) {
    filtered = filtered.filter((item) => item.location && locations.includes(item.location));
  }
  return filtered;
};

/**
 * The single-select characteristic filters, in the order the UI cascades them.
 * Geography (origins/terminals) sits above the whole chain.
 */
const TERMINAL_CASCADE_TAIL = [
  'storage', 'condition', 'environment', 'quality', 'appearance', 'properties',
];
const TERMINAL_CASCADE_ORDER = [
  'type', 'variety', 'size', 'grade', 'package', ...TERMINAL_CASCADE_TAIL,
];
const TERMINAL_CASCADE_CUCUMBER = [
  'type', 'variety', 'sizeGroup', 'package', 'size', 'grade', ...TERMINAL_CASCADE_TAIL,
];

const CASCADE_MULTI_KEYS = new Set(['variety', 'sizeGroup', 'size', 'grade', 'storage', 'condition', 'environment', 'quality', 'appearance', 'properties']);

const cascadeField = (key, readSize) => {
  if (key === 'size') return readSize || 'item_size';
  if (key === 'sizeGroup') return 'size_group';
  return key;
};

const readCascadeValue = (item, key, readSize) => {
  const field = cascadeField(key, readSize);
  if (typeof field === 'function') return field(item);
  if (key === 'package') return item.package || item.sku;
  return item[field];
};

/** Restrict rows by one cascade filter, matching how the chart itself filters. */
const applyTerminalCascadeFilter = (rows, key, value, readSize, sizeRules) => {
  if (Array.isArray(value)) {
    if (sizeRules && key === 'size') {
      const opts = getCleanOptions(rows, readSize || 'item_size');
      return sizeRules.applySizeFilter(rows, value, opts, readSize);
    }
    if (sizeRules && key === 'sizeGroup') {
      const groupRead = sizeRules.readSizeGroup || readSizeGroup;
      const opts = getCleanOptions(rows, groupRead);
      return sizeRules.applySizeGroupFilter(rows, value, opts, groupRead);
    }
    if (!value.length) return rows;
    if (key === 'package') {
      return rows.filter((item) => value.includes(item.package) || value.includes(item.sku));
    }
    return rows.filter((item) => value.includes(readCascadeValue(item, key, readSize)));
  }
  if (!value) return rows;
  switch (key) {
    case 'type': return rows.filter((item) => !item.type || item.type === value);
    case 'package': return rows.filter((item) => item.package === value || item.sku === value);
    default: return rows.filter((item) => readCascadeValue(item, key, readSize) === value);
  }
};

/**
 * Default type / variety / … for Terminal Price Pulse, when all origins and
 * terminals apply.
 *
 * Previously this maximised the mean of max(low, high) package price, one field
 * at a time. That is the wrong objective: Organic outprices Conventional almost
 * everywhere, so the chart opened on a thin premium slice rather than the data
 * the user came to see. It now scores whole combinations by how many days they
 * actually plot — see `pickBestCombination`.
 */
function isDefaultNyLaTerminal(location) {
  const s = String(location || '').toLowerCase();
  return s.includes('new york') || s.includes('los angeles');
}

function pickDefaultTerminalLocations(all) {
  const list = Array.isArray(all) ? all : [];
  const nyLa = list.filter(isDefaultNyLaTerminal);
  return nyLa.length ? nyLa : list;
}

const computeTerminalDefaultCascadeByMostData = (data, priceTypes) => {
  if (!data?.length) return null;
  return pickBestTerminalCombination(data, { priceTypes });
};

/**
 * Terminal Price Pulse Chart - Shows terminal price trends by location and origin
 */
const TerminalPriceLineChart = ({
  data = [],
  email = '',
  dateStart = null,
  dateEnd = null,
  commodity = '',
  forceFiltersOpen = false,
  presetScorecards = null,
  hidePresetScorecards = false,
  presetDescription = '',
  hideLegend = false,
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
  fitXToWidth = false,
}) => {
  const sizeRules = commoditySizeRules(commodity);
  const readSize = sizeRules?.readSize || sizeReader(commodity);
  const cascadeOrder = sizeRules ? TERMINAL_CASCADE_CUCUMBER : TERMINAL_CASCADE_ORDER;
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('terminal-price-line');
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  // Collapsed by default. In fullscreen the filters still show on desktop (lg+)
  // via a CSS override below, but on small screens (e.g. the portrait panel)
  // they stay collapsed behind the Filters toggle for a cleaner view.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const scrollViewportRef = useRef(null);
  useSyncedChartScroll(scrollViewportRef, 'price-pulse-scroll');
  const { trigger: tooltipTrigger, tooltipDismissKey, tooltipActive, tooltipPosition, tooltipSnap, tooltipCursorX, tooltipPinPayload, pinTooltip, markTapStart } = useChartTooltipTrigger(scrollViewportRef);
  const isBelowLg = useIsBelowLg();
  const isPhone = useIsPhone();

  // Filter states
  const [selectedPriceTypes, setSelectedPriceTypes] = useState(['low_price', 'high_price']);
  const [priceUnit, setPriceUnit] = useState('package'); // 'package', 'kg', 'lbs'
  const [selectedType, setSelectedType] = useState('Conventional');
  const [selectedVariety, setSelectedVariety] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]); // Terminal locations
  const [selectedOrigins, setSelectedOrigins] = useState([]); // Origins (where commodity came from)
  const [selectedSizeGroup, setSelectedSizeGroup] = useState([]);
  const [selectedSize, setSelectedSize] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const lastPackageRef = useRef('');
  const [selectedStorage, setSelectedStorage] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState([]);
  const [selectedAppearance, setSelectedAppearance] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  /** Explicit Clear All on terminals / origins (vs [] from incomplete first persist). */
  const [locationsUserCleared, setLocationsUserCleared] = useState(false);
  const [originsUserCleared, setOriginsUserCleared] = useState(false);

  const linePriceTooltipContent = useCallback(
    (props) => {
      const src = tooltipSnap || props;
      // Sort tooltip rows ASC by terminal so (e.g.) Atlanta's high+low group
      // together. Derive the terminal from the raw series key
      // (dataKey = "Origin_low_price_Location"), NOT the display name — the
      // label omits the location when it's unambiguous (see
      // getLineDisplayName below), so parsing the name would group by price
      // type instead of terminal. Must stay dataKey-based even after the B6
      // legend-name shortening.
      const terminalOf = (entry) => {
        const key = String(entry?.dataKey || entry?.name || '');
        const lowIdx = key.lastIndexOf('_low_price_');
        const highIdx = key.lastIndexOf('_high_price_');
        if (lowIdx !== -1) return key.slice(lowIdx + 11);
        if (highIdx !== -1) return key.slice(highIdx + 12);
        return key;
      };
      const sortedPayload = Array.isArray(src.payload)
        ? [...src.payload].sort((a, b) =>
            terminalOf(a).localeCompare(terminalOf(b)) ||
            String(a.name || '').localeCompare(String(b.name || ''))
          )
        : src.payload;
      return (
        <LinePriceTooltipBody {...src} payload={sortedPayload} theme={theme} fontSize={fontSize} commodity={commodity} />
      );
    },
    [theme, fontSize, tooltipSnap, commodity]
  );

  // Modal states
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [sizeGroupModalOpen, setSizeGroupModalOpen] = useState(false);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [priceUnitModalOpen, setPriceUnitModalOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(false);
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  const initializedRef = useRef(false);

  // Reset init guard when commodity scope changes so defaults can apply for the new commodity.
  useLayoutEffect(() => {
    initializedRef.current = false;
  }, [commodity]);

  // Check if price unit fields are available
  const priceUnitFieldsAvailable = useMemo(() => {
    if (!data || data.length === 0) return false;
    // Check if at least one item has all price unit fields
    return data.some((item) =>
      item.low_price_lbs != null &&
      item.high_price_lbs != null &&
      item.low_price_kg != null &&
      item.high_price_kg != null
    );
  }, [data]);

  // Cascade order: Origin → Terminal (geography), then Type → Variety → Size → Grade → Package →
  // Storage → Condition → Environment → Quality → Appearance → Properties
  const availableOptions = useMemo(() => {
    if (!data || data.length === 0) {
      return { types: [], varieties: [], locations: [], origins: [], sizeGroups: [], sizes: [], grades: [], packages: [],
               storages: [], conditions: [], environments: [], qualities: [],
               appearances: [], properties: [] };
    }

    const origins = getCleanOptions(data, 'origin', commodity);
    let afterOrigins = data;
    if (selectedOrigins.length > 0) {
      afterOrigins = afterOrigins.filter((item) => item.origin && selectedOrigins.includes(item.origin));
    }
    const locations = getCleanOptions(afterOrigins, 'location', commodity);

    let filtered = applyGeographicDataFilter(data, selectedOrigins, selectedLocations);
    const types = getCleanOptions(filtered, 'type', commodity);
    if (selectedType) filtered = filtered.filter((item) => !item.type || item.type === selectedType);
    const varieties = getCleanOptions(filtered, 'variety', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'variety', selectedVariety, varieties);
    let sizeGroups = [];
    let sizes = [];
    if (sizeRules) {
      const groupRead = sizeRules.readSizeGroup;
      sizeGroups = getCleanOptions(filtered, groupRead, commodity);
      sizes = getCleanOptions(filtered, readSize, commodity);
      filtered = sizeRules.applySizeGroupFilter(filtered, selectedSizeGroup, sizeGroups, groupRead);
      filtered = sizeRules.applySizeFilter(filtered, selectedSize, sizes, readSize);
    }
    let packages = [];
    const pinPackages = () => {
      if (!packagePinActive(priceUnit)) return;
      packages = [...new Set(filtered.map((d) => d.package || d.sku || '').filter((v) => v))].sort();
      if (selectedPackage) {
        filtered = filtered.filter((item) => item.package === selectedPackage || item.sku === selectedPackage);
      }
    };
    if (sizeRules) pinPackages();
    if (!sizeRules) {
      sizes = getCleanOptions(filtered, 'item_size', commodity);
      filtered = applyMultiOrAllFilter(filtered, 'item_size', selectedSize, sizes);
    }
    const grades = getCleanOptions(filtered, 'grade', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'grade', selectedGrade, grades);
    if (!sizeRules) pinPackages();
    const storages = getCleanOptions(filtered, 'storage', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'storage', selectedStorage, storages);
    const conditions = getCleanOptions(filtered, 'condition', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'condition', selectedCondition, conditions);
    const environments = getCleanOptions(filtered, 'environment', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'environment', selectedEnvironment, environments);
    const qualities = getCleanOptions(filtered, 'quality', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'quality', selectedQuality, qualities);
    const appearances = getCleanOptions(filtered, 'appearance', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'appearance', selectedAppearance, appearances);
    const properties = getCleanOptions(filtered, 'properties', commodity);
    filtered = applyMultiOrAllFilter(filtered, 'properties', selectedProperties, properties);

    return { types, varieties, locations, origins, sizeGroups, sizes, grades, packages,
             storages, conditions, environments, qualities,
             appearances, properties };
  }, [data, selectedOrigins, selectedLocations, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality, selectedAppearance, selectedProperties, priceUnit, sizeRules, commodity, readSize]);

  /**
   * Re-derive every filter below `level` so the chart lands on the most data
   * available inside the user's choice.
   *
   * These filters used to be kept whenever the old value still appeared in the
   * new dropdown ("keep-if-valid"). Dropdown membership is not coverage:
   * switching Type to Organic kept a Size and SKU that do exist under Organic
   * but intersect it on only a handful of days, so the chart went nearly empty
   * while every filter still looked reasonable. Everything below the changed
   * filter is now re-scored on days actually plotted, the same way the initial
   * default is chosen — so a reload reproduces what was on screen.
   *
   * @param {string|null} level — the filter the user changed; null means
   *   geography (origins/terminals), which sits above the whole chain
   * @param {object} [overrides] — values not yet flushed to state, keyed as in
   *   cascadeOrder plus `origins` / `locations`
   */
  const cascadeBelow = useCallback((level, overrides = {}) => {
    if (!data || data.length === 0) return;

    const current = {
      type: selectedType,
      variety: selectedVariety,
      sizeGroup: selectedSizeGroup,
      size: selectedSize,
      grade: selectedGrade,
      package: selectedPackage,
      storage: selectedStorage,
      condition: selectedCondition,
      environment: selectedEnvironment,
      quality: selectedQuality,
      appearance: selectedAppearance,
      properties: selectedProperties,
    };
    const valueFor = (key) => (overrides[key] !== undefined ? overrides[key] : current[key]);

    const origins = overrides.origins !== undefined ? overrides.origins : selectedOrigins;
    const locations = overrides.locations !== undefined ? overrides.locations : selectedLocations;
    let rows = applyGeographicDataFilter(data, origins, locations);

    // Pin the changed filter and everything above it, then score the rest.
    const cut = level === null ? 0 : cascadeOrder.indexOf(level) + 1;
    for (let i = 0; i < cut; i++) {
      const key = cascadeOrder[i];
      const unit = overrides.priceUnit !== undefined ? overrides.priceUnit : priceUnit;
      if (key === 'package' && unit !== 'package') continue;
      rows = applyTerminalCascadeFilter(rows, key, valueFor(key), readSize, sizeRules);
    }

    const priceTypes = selectedPriceTypes.length > 0 ? selectedPriceTypes : ['low_price', 'high_price'];
    const best = pickBestTerminalCombination(rows, { priceTypes });

    const setters = {
      type: setSelectedType,
      variety: setSelectedVariety,
      sizeGroup: setSelectedSizeGroup,
      size: setSelectedSize,
      grade: setSelectedGrade,
      package: setSelectedPackage,
      storage: setSelectedStorage,
      condition: setSelectedCondition,
      environment: setSelectedEnvironment,
      quality: setSelectedQuality,
      appearance: setSelectedAppearance,
      properties: setSelectedProperties,
    };
    for (let i = cut; i < cascadeOrder.length; i++) {
      const key = cascadeOrder[i];
      if (key === 'package') continue;
      if (sizeRules && key === 'size' && level === 'sizeGroup') continue;
      if (CASCADE_MULTI_KEYS.has(key)) {
        const nextOpts = getCleanOptions(
          rows,
          key === 'sizeGroup' ? sizeRules.readSizeGroup : cascadeField(key, readSize),
          commodity
        );
        if (sizeRules && (key === 'size' || key === 'sizeGroup')) {
          setters[key](sizeRules.keepOrAll(valueFor(key), nextOpts));
        } else {
          setters[key](nextOpts);
        }
      } else {
        const cur = valueFor(key);
        const still = cur && rows.some((item) => {
          if (key === 'package') return item.package === cur || item.sku === cur;
          return readCascadeValue(item, key, readSize) === cur;
        });
        setters[key](still ? cur : (best[key] || ''));
      }
    }
  }, [
    data, selectedOrigins, selectedLocations, selectedPriceTypes, priceUnit, cascadeOrder, readSize, commodity,
    selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
    selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
    selectedAppearance, selectedProperties, sizeRules,
  ]);

  useEffect(() => {
    if (!sizeRules) return;
    const nextSize = sizeRules.keepOrAll(selectedSize, availableOptions.sizes || []);
    if (JSON.stringify(nextSize) !== JSON.stringify(selectedSize)) setSelectedSize(nextSize);
    const nextGroup = sizeRules.keepOrAll(selectedSizeGroup, availableOptions.sizeGroups || []);
    if (JSON.stringify(nextGroup) !== JSON.stringify(selectedSizeGroup)) setSelectedSizeGroup(nextGroup);
  }, [availableOptions.sizes, availableOptions.sizeGroups, selectedSize, selectedSizeGroup, sizeRules]);

  // Switching price unit back to package restores the SKU the user last had,
  // when it still exists in scope; everything below it is re-scored.
  const restorePackageSelection = useCallback(() => {
    if (!data || data.length === 0) return;
    let rows = applyGeographicDataFilter(data, selectedOrigins, selectedLocations);
    const upstream = {
      type: selectedType, variety: selectedVariety, sizeGroup: selectedSizeGroup, size: selectedSize, grade: selectedGrade,
    };
    for (const key of cascadeOrder.filter((k) => k !== 'package' && TERMINAL_CASCADE_TAIL.indexOf(k) === -1)) {
      rows = applyTerminalCascadeFilter(rows, key, upstream[key], readSize, sizeRules);
    }
    const nextPackage = pickPackageWithMostRecentMonthPoints(rows);
    setSelectedPackage(nextPackage);
    cascadeBelow('package', { package: nextPackage, priceUnit: 'package' });
  }, [data, selectedOrigins, selectedLocations, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, cascadeBelow, cascadeOrder, readSize, sizeRules]);

  const hydrateTerminalPriceLine = useCallback(
    (s) => {
      initializedRef.current = true;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      const validPt = ['low_price', 'high_price'];
      // Default / legacy: always show both low and high terminal prices.
      const savedPt = Array.isArray(s.selectedPriceTypes)
        ? s.selectedPriceTypes.filter((p) => validPt.includes(p))
        : [];
      const priceTypes = savedPt.length === 2 ? savedPt : ['low_price', 'high_price'];
      setSelectedPriceTypes(priceTypes);
      setPriceUnit('kg');

      const allOrigins = getCleanOptions(data, 'origin');
      const originOk = (o) => allOrigins.some((ao) => String(ao) === String(o));
      const originsCleared = s.originsUserCleared === true;
      setOriginsUserCleared(originsCleared);
      let origins;
      if (originsCleared) {
        origins = Array.isArray(s.selectedOrigins) ? s.selectedOrigins.filter(originOk) : [];
      } else if (Array.isArray(s.selectedOrigins) && s.selectedOrigins.length > 0) {
        origins = s.selectedOrigins.filter(originOk);
      } else {
        origins = allOrigins;
      }
      setSelectedOrigins(origins);

      let rows = data;
      if (origins.length > 0) {
        rows = rows.filter((item) => item.origin && origins.includes(item.origin));
      }

      // Every load opens on the combination with the most data, ignoring the
      // characteristics saved from the last visit. Restoring them meant a user
      // who had drilled into a thin slice (say Organic on a commodity that is
      // mostly Conventional) came back to a nearly empty chart and read it as
      // broken. Filtering is a within-session action: pick a type, see that
      // type; reload, and the chart is full again.
      const restored = pickBestTerminalCombination(rows, { priceTypes });

      setSelectedType(restored.type || 'Conventional');
      {
        let scoped = rows;
        if (restored.type) scoped = scoped.filter((item) => !item.type || item.type === restored.type);
        setSelectedVariety(defaultPulseVarieties(scoped));
        if (sizeRules) {
          setSelectedSizeGroup(getCleanOptions(scoped, sizeRules.readSizeGroup, commodity));
          setSelectedSize(getCleanOptions(scoped, readSize, commodity));
        } else {
          setSelectedSizeGroup([]);
          setSelectedSize(getCleanOptions(scoped, 'item_size', commodity));
        }
        setSelectedGrade(getCleanOptions(scoped, 'grade'));
        setSelectedStorage(getCleanOptions(scoped, 'storage'));
        setSelectedCondition(getCleanOptions(scoped, 'condition'));
        setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
        setSelectedQuality(getCleanOptions(scoped, 'quality'));
        setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
        setSelectedProperties(getCleanOptions(scoped, 'properties'));
      }
      setSelectedPackage(restored.package);

      // Terminals are an upstream geography filter (paired with Origins). Do not validate/prune terminals
      // against downstream SKU filters (type/variety/size/...), otherwise selecting an "incompatible"
      // terminal gets immediately undone by persistence hydration.
      const rowsForLocations =
        origins.length > 0 ? data.filter((item) => item.origin && origins.includes(item.origin)) : data;
      const locationOptions = getCleanOptions(rowsForLocations, 'location');
      const locOk = (l) => locationOptions.some((lo) => String(lo) === String(l));
      const locsCleared = s.locationsUserCleared === true;
      setLocationsUserCleared(locsCleared);
      let locs;
      if (locsCleared) {
        locs = Array.isArray(s.selectedLocations) ? s.selectedLocations.filter(locOk) : [];
      } else if (Array.isArray(s.selectedLocations) && s.selectedLocations.length > 0) {
        locs = s.selectedLocations.filter(locOk);
        if (locs.length === locationOptions.length) {
          locs = pickDefaultTerminalLocations(locationOptions);
        }
      } else {
        locs = pickDefaultTerminalLocations(locationOptions);
      }
      setSelectedLocations(locs);
      {
        let varietyScope = applyGeographicDataFilter(data, origins, locs);
        if (restored.type) varietyScope = varietyScope.filter((item) => !item.type || item.type === restored.type);
        setSelectedVariety(defaultPulseVarieties(varietyScope));
      }

      // Match init defaults when snapshot had empty terminal/origin lists without explicit clear (junk / first persist).
      if (
        !locsCleared &&
        !originsCleared &&
        (!Array.isArray(s.selectedLocations) || s.selectedLocations.length === 0) &&
        (!Array.isArray(s.selectedOrigins) || s.selectedOrigins.length === 0)
      ) {
        const defaultPriceTypes = ['low_price', 'high_price'];
        setSelectedPriceTypes(defaultPriceTypes);
        setPriceUnit('kg');
        setSelectedType('Conventional');
        const cascade = computeTerminalDefaultCascadeByMostData(data, defaultPriceTypes);
        if (cascade) {
          setSelectedType(cascade.type);
          {
            let scoped = applyGeographicDataFilter(data, origins, locs);
            if (cascade.type) scoped = scoped.filter((item) => !item.type || item.type === cascade.type);
            setSelectedVariety(defaultPulseVarieties(scoped));
            if (sizeRules) {
              setSelectedSizeGroup(getCleanOptions(scoped, sizeRules.readSizeGroup, commodity));
              setSelectedSize(getCleanOptions(scoped, readSize, commodity));
            } else {
              setSelectedSizeGroup([]);
              setSelectedSize(getCleanOptions(scoped, 'item_size', commodity));
            }
            setSelectedGrade(getCleanOptions(scoped, 'grade'));
            setSelectedStorage(getCleanOptions(scoped, 'storage'));
            setSelectedCondition(getCleanOptions(scoped, 'condition'));
            setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
            setSelectedQuality(getCleanOptions(scoped, 'quality'));
            setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
            setSelectedProperties(getCleanOptions(scoped, 'properties'));
          }
          setSelectedPackage(cascade.package);
        }
      }
    },
    [data, sizeRules, commodity, readSize]
  );

  const snapshotTerminalPriceLine = useCallback(
    () => ({
      filtersExpanded,
      selectedPriceTypes,
      priceUnit,
      selectedType,
      selectedVariety,
      selectedLocations,
      locationsUserCleared,
      selectedOrigins,
      originsUserCleared,
      selectedSizeGroup,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    }),
    [
      filtersExpanded,
      selectedPriceTypes,
      priceUnit,
      selectedType,
      selectedVariety,
      selectedLocations,
      locationsUserCleared,
      selectedOrigins,
      originsUserCleared,
      selectedSizeGroup,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  usePersistWidgetSnapshot(
    'terminal-price-line',
    commodity,
    !!(data && data.length > 0),
    hydrateTerminalPriceLine,
    snapshotTerminalPriceLine,
    [
      filtersExpanded,
      selectedPriceTypes,
      priceUnit,
      selectedType,
      selectedVariety,
      selectedLocations,
      locationsUserCleared,
      selectedOrigins,
      originsUserCleared,
      selectedSizeGroup,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  // Initialize default selections
  useEffect(() => {
    if (!analyticsPrefsReady) return;
    if (!data || data.length === 0) return;
    if (initializedRef.current) return;

    initializedRef.current = true;
    setLocationsUserCleared(false);
    setOriginsUserCleared(false);

    // Defaults:
    // - Find: low + high price
    // - Price unit: /kg
    // - Origin: all
    // - Terminal: New York + Los Angeles (else all if those names are missing)
    // - Variety: two with the most points in the last 31 days
    const defaultPriceTypes = ['low_price', 'high_price'];
    setSelectedPriceTypes(defaultPriceTypes);
    setPriceUnit('kg');

    const allLocations = getCleanOptions(data, 'location');
    const defaultLocs = pickDefaultTerminalLocations(allLocations);
    setSelectedLocations(defaultLocs);

    // Set all origins by default
    const allOrigins = getCleanOptions(data, 'origin');
    setSelectedOrigins(allOrigins);

    // Type falls out of the scoring like every other field (Conventional only
    // breaks exact ties), so this is just the fallback when there is no data.
    setSelectedType('Conventional');
    // Type + tail dims: most days plotted across all origins × terminals.
    const cascade = computeTerminalDefaultCascadeByMostData(data, defaultPriceTypes);
    if (cascade) {
      setSelectedType(cascade.type);
      {
        let scoped = applyGeographicDataFilter(data, allOrigins, defaultLocs);
        if (cascade.type) scoped = scoped.filter((item) => !item.type || item.type === cascade.type);
        setSelectedVariety(defaultPulseVarieties(scoped));
        if (sizeRules) {
          setSelectedSizeGroup(getCleanOptions(scoped, sizeRules.readSizeGroup, commodity));
          setSelectedSize(getCleanOptions(scoped, readSize, commodity));
        } else {
          setSelectedSizeGroup([]);
          setSelectedSize(getCleanOptions(scoped, 'item_size', commodity));
        }
        setSelectedGrade(getCleanOptions(scoped, 'grade'));
        setSelectedStorage(getCleanOptions(scoped, 'storage'));
        setSelectedCondition(getCleanOptions(scoped, 'condition'));
        setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
        setSelectedQuality(getCleanOptions(scoped, 'quality'));
        setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
        setSelectedProperties(getCleanOptions(scoped, 'properties'));
      }
      setSelectedPackage(cascade.package);
    }
  }, [analyticsPrefsReady, data]);

  // Filter and transform data for chart
  const { chartData, lineKeys, seriesTotal, seriesCapped } = useMemo(() => {
    if (!data || data.length === 0 || selectedLocations.length === 0 || selectedOrigins.length === 0 || selectedPriceTypes.length === 0) {
      return { chartData: [], lineKeys: [], seriesTotal: 0, seriesCapped: false };
    }

    const getPriceField = (item, priceTypeValue) => {
      if (priceUnit === 'package') {
        return item[priceTypeValue];
      } else if (priceUnit === 'kg') {
        return priceTypeValue === 'low_price' ? item.low_price_kg : item.high_price_kg;
      } else if (priceUnit === 'lbs') {
        return priceTypeValue === 'low_price' ? item.low_price_lbs : item.high_price_lbs;
      }
      return item[priceTypeValue];
    };

    const filteredData = data.filter((item) => {
      if (item.type && selectedType && item.type !== selectedType) return false;
      if (shouldNameSeriesForMulti(selectedVariety, availableOptions.varieties) && !selectedVariety.includes(item.variety)) return false;
      if (sizeRules && !sizeRules.sizeGroupKeepsRow(item, selectedSizeGroup, availableOptions.sizeGroups, sizeRules.readSizeGroup)) return false;
      if (sizeRules) {
        if (!sizeRules.sizeKeepsRow(item, selectedSize, availableOptions.sizes, readSize)) return false;
      } else if (shouldNameSeriesForMulti(selectedSize, availableOptions.sizes) && !selectedSize.includes(item.item_size)) return false;
      if (shouldNameSeriesForMulti(selectedGrade, availableOptions.grades) && !selectedGrade.includes(item.grade)) return false;
      if (priceUnit === 'package' && selectedPackage && (item.package !== selectedPackage && item.sku !== selectedPackage)) return false;
      if (selectedLocations.length > 0 && !selectedLocations.includes(item.location)) return false;
      if (selectedOrigins.length > 0 && !selectedOrigins.includes(item.origin)) return false;
      if (shouldNameSeriesForMulti(selectedStorage, availableOptions.storages) && !selectedStorage.includes(item.storage)) return false;
      if (shouldNameSeriesForMulti(selectedCondition, availableOptions.conditions) && !selectedCondition.includes(item.condition)) return false;
      if (shouldNameSeriesForMulti(selectedEnvironment, availableOptions.environments) && !selectedEnvironment.includes(item.environment)) return false;
      if (shouldNameSeriesForMulti(selectedQuality, availableOptions.qualities) && !selectedQuality.includes(item.quality)) return false;
      if (shouldNameSeriesForMulti(selectedAppearance, availableOptions.appearances) && !selectedAppearance.includes(item.appearance)) return false;
      if (shouldNameSeriesForMulti(selectedProperties, availableOptions.properties) && !selectedProperties.includes(item.properties)) return false;
      return true;
    });

    const months = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };

    const toLocalMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const dayKeyFromDate = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    const parseDateLabel = (str) => {
      const parts = String(str || '').trim().split(/\s+/);
      if (parts.length < 2) return null;
      const day = parseInt(parts[0], 10);
      const month = months[parts[1]];
      if (Number.isNaN(day) || month === undefined) return null;
      const year = parts.length >= 3 ? parseInt(parts[2], 10) : null;
      const yearValid = year != null && !Number.isNaN(year) ? year : null;
      return { day, month, year: yearValid };
    };

    const inferYearForMonthDay = (month, day, start, end) => {
      const startY = start.getFullYear();
      let candidate = toLocalMidnight(new Date(startY, month, day));
      if (candidate < start) candidate = toLocalMidnight(new Date(startY + 1, month, day));
      if (candidate > end) candidate = toLocalMidnight(new Date(startY - 1, month, day));
      return candidate;
    };

    const sortKeyFromLabel = (str) => {
      const p = parseDateLabel(str);
      if (!p) return 0;
      const y = p.year ?? new Date().getFullYear();
      return new Date(y, p.month, p.day).getTime();
    };

    const lineKeysSet = new Set();
    const skuSeen = new Set();
    const hasRange = !!dateStart && !!dateEnd;
    const extraSeries = (item) => comboSuffix(item, priceUnit === 'package');
    const makeLineKey = (origin, priceTypeValue, location, item) =>
      `${origin}_${priceTypeValue}_${location}${extraSeries(item)}`;

    if (hasRange) {
      const start = toLocalMidnight(new Date(dateStart));
      const end = toLocalMidnight(new Date(dateEnd));

      const dateMap = new Map();
      let cur = new Date(start);
      while (cur <= end) {
        const key = dayKeyFromDate(cur);
        const dayCopy = new Date(cur);
        dateMap.set(key, {
          date: formatDateShort(dayCopy),
          sortKey: dayCopy.getTime(),
          rawDate: dayCopy,
        });
        cur.setDate(cur.getDate() + 1);
      }

      filteredData.forEach((item) => {
        const origin = item.origin || 'Unknown';
        const location = item.location || 'Unknown';
        const parsed = parseDateLabel(item.date_string);
        if (!parsed) return;

        let candidateDate;
        if (parsed.year != null) {
          candidateDate = toLocalMidnight(new Date(parsed.year, parsed.month, parsed.day));
          if (candidateDate < start || candidateDate > end) {
            candidateDate = inferYearForMonthDay(parsed.month, parsed.day, start, end);
          }
        } else {
          candidateDate = inferYearForMonthDay(parsed.month, parsed.day, start, end);
        }

        const key = dayKeyFromDate(candidateDate);
        const dateEntry = dateMap.get(key);
        if (!dateEntry) return;

        selectedPriceTypes.forEach((priceTypeValue) => {
          const priceValue = getPriceField(item, priceTypeValue);
          if (typeof priceValue === 'number' && !Number.isNaN(priceValue)) {
            const lineKey = makeLineKey(origin, priceTypeValue, location, item);
            lineKeysSet.add(lineKey);
            pushPulseSku(dateEntry, lineKey, pulseSkuFromItem(item, priceTypeValue, priceValue), skuSeen);
            if (dateEntry[lineKey] !== undefined) {
              dateEntry[lineKey] = (dateEntry[lineKey] + priceValue) / 2;
            } else {
              dateEntry[lineKey] = priceValue;
            }
          }
        });
      });

      const chartData = Array.from(dateMap.values()).sort((a, b) => a.sortKey - b.sortKey);
      const keys = Array.from(lineKeysSet).sort();
      return { chartData, lineKeys: keys, seriesTotal: keys.length, seriesCapped: false };
    }

    const dateMap = new Map();
    filteredData.forEach((item) => {
      const dateKey = item.date_string;
      const origin = item.origin || 'Unknown';
      const location = item.location || 'Unknown';

      selectedPriceTypes.forEach((priceTypeValue) => {
        const priceValue = getPriceField(item, priceTypeValue);
        if (typeof priceValue === 'number' && !Number.isNaN(priceValue)) {
          const lineKey = makeLineKey(origin, priceTypeValue, location, item);
          lineKeysSet.add(lineKey);

          if (!dateMap.has(dateKey)) {
            const sortKey = sortKeyFromLabel(dateKey);
            dateMap.set(dateKey, { date: dateKey, sortKey, rawDate: sortKey ? new Date(sortKey) : null });
          }
          const dateEntry = dateMap.get(dateKey);
          pushPulseSku(dateEntry, lineKey, pulseSkuFromItem(item, priceTypeValue, priceValue), skuSeen);
          dateEntry[lineKey] = priceValue;
        }
      });
    });

    const chartData = Array.from(dateMap.values()).sort((a, b) => a.sortKey - b.sortKey);
    const keys = Array.from(lineKeysSet).sort();
    return { chartData, lineKeys: keys, seriesTotal: keys.length, seriesCapped: false };
  }, [data, selectedPriceTypes, priceUnit, selectedType, selectedVariety, selectedLocations, selectedOrigins, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality, selectedAppearance, selectedProperties, dateStart, dateEnd, availableOptions, sizeRules, readSize]);

  // Add half-day padding on both sides so duration matches Volume-by-Origin bars
  const xAxisConfig = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return { ticks: undefined, domain: undefined };
    }
    const xs = chartData
      .map((d) => (typeof d.sortKey === 'number' ? d.sortKey : null))
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (xs.length === 0) return { ticks: undefined, domain: undefined };

    const min = Math.min(...xs);
    const max = Math.max(...xs);
    const halfDayMs = 12 * 60 * 60 * 1000;
    const ticks = chartData
      .filter((d) => d.date && typeof d.sortKey === 'number')
      .map((d) => d.sortKey);

    return { ticks, domain: [min - halfDayMs, max + halfDayMs] };
  }, [chartData]);

  const plotAreaRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = plotAreaRef.current;
    if (!el) return;

    let rafId = null;
    let rafRetryId = null;
    const updateWidth = (next) => {
      const width = typeof next === 'number' ? next : el.getBoundingClientRect().width;
      if (!Number.isFinite(width) || width <= 0) return;
      setContainerWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width));
    };

    // Ensure we measure after layout settles (important when navigating into Analytics)
    rafId = window.requestAnimationFrame(() => updateWidth());
    // Retry a few frames until we get a non-zero width (some layouts report 0 on first paint)
    let attempts = 0;
    const retry = () => {
      attempts += 1;
      updateWidth();
      if ((el.getBoundingClientRect().width || 0) <= 0 && attempts < 10) {
        rafRetryId = window.requestAnimationFrame(retry);
      }
    };
    rafRetryId = window.requestAnimationFrame(retry);

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        const w = entry?.contentRect?.width;
        updateWidth(w);
      });
      ro.observe(el);
    }

    window.addEventListener('resize', updateWidth);
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (rafRetryId != null) window.cancelAnimationFrame(rafRetryId);
      window.removeEventListener('resize', updateWidth);
      if (ro) ro.disconnect();
    };
  }, []);

  const { needsScroll: needsScrollRaw, chartWidth, minWidthNeeded } = useMemo(
    () =>
      getLineChartHorizontalScrollDimensions({
        dataPointCount: chartData.length,
        containerWidth,
      }),
    [chartData.length, containerWidth]
  );
  const needsScroll = needsScrollRaw && !fitXToWidth;

  // Phones size the price axis to the labels it actually draws instead of the
  // fixed 72px, which is mostly empty at the smaller phone tick size.
  //
  // Preset views only: the widget dashboard renders a rotated axis title in
  // that same column (the `!forceFiltersOpen` branch below). Rotated about its
  // centre, that title's horizontal extent sits mid-column, so narrowing the
  // column would run it straight into the tick labels.
  const compactYAxis = isPhone && forceFiltersOpen;
  const priceYAxisWidth = useMemo(
    () =>
      getChartYAxisWidth({
        isPhone: compactYAxis,
        fontSize,
        labels: getYAxisDomainLabels(getSmartYDomain(chartData, lineKeys), formatPriceTick),
      }),
    [compactYAxis, fontSize, chartData, lineKeys]
  );
  const scrollYAxisGutterPx = getChartScrollYAxisGutterPx({
    isPhone: compactYAxis,
    yAxisWidth: priceYAxisWidth,
  });

  const effectiveMinWidth = useMemo(() => {
    const mw = Number(minWidthNeeded) || 0;
    const cw = Number(containerWidth) || 0;
    const v = Math.max(mw, cw);
    return v > 0 ? v : undefined;
  }, [minWidthNeeded, containerWidth]);

  const priceAxisLabelText = useMemo(
    () =>
      priceUnit === 'package'
        ? t('filters.price')
        : `${t('filters.price')} ($${priceUnit === 'kg' ? '/kg' : '/lb'})`,
    [t, priceUnit]
  );

  /** Gutter Y-axis chart: vertical margins tuned so axis spine length + endpoints match main chart vertical grid lines. */
  const pulseGridChartRef = useRef(null);
  const pulseYAxisChartRef = useRef(null);
  const [gutterPlotMarginsY, setGutterPlotMarginsY] = useState({
    top: PULSE_CHART_MARGIN_Y_DEFAULT,
    bottom: PULSE_CHART_MARGIN_Y_DEFAULT,
  });

  const pulseYAxisSyncLayoutKey = useMemo(
    () =>
      [
        needsScroll,
        chartWidth,
        containerWidth,
        chartData.length,
        lineKeys.join('\u0000'),
        `${getSmartYDomain(chartData, lineKeys)?.[0]}-${getSmartYDomain(chartData, lineKeys)?.[1]}`,
        fontSize,
        filtersExpanded,
        priceUnit,
      ].join('|'),
    [needsScroll, chartWidth, containerWidth, chartData.length, lineKeys, chartData, fontSize, filtersExpanded, priceUnit]
  );

  useLayoutEffect(() => {
    if (!needsScroll) {
      setGutterPlotMarginsY((p) =>
        p.top === PULSE_CHART_MARGIN_Y_DEFAULT && p.bottom === PULSE_CHART_MARGIN_Y_DEFAULT
          ? p
          : { top: PULSE_CHART_MARGIN_Y_DEFAULT, bottom: PULSE_CHART_MARGIN_Y_DEFAULT }
      );
      return;
    }
    setGutterPlotMarginsY((p) =>
      p.top === PULSE_CHART_MARGIN_Y_DEFAULT && p.bottom === PULSE_CHART_MARGIN_Y_DEFAULT
        ? p
        : { top: PULSE_CHART_MARGIN_Y_DEFAULT, bottom: PULSE_CHART_MARGIN_Y_DEFAULT }
    );
  }, [needsScroll, pulseYAxisSyncLayoutKey]);

  useLayoutEffect(() => {
    if (!needsScroll) return;

    let cancelled = false;
    const measureAndSync = () => {
      if (cancelled) return;
      const gridRoot = pulseGridChartRef.current;
      const axisRoot = pulseYAxisChartRef.current;
      if (!gridRoot || !axisRoot) return;

      const gridLines = gridRoot.querySelectorAll('.recharts-cartesian-grid-vertical line');
      let gridLine = null;
      for (let i = 0; i < gridLines.length; i++) {
        if (isVerticalSvgLine(gridLines[i])) {
          gridLine = gridLines[i];
          break;
        }
      }

      const axisLines = axisRoot.querySelectorAll('line.recharts-cartesian-axis-line');
      let axisLine = null;
      for (let i = 0; i < axisLines.length; i++) {
        if (isVerticalSvgLine(axisLines[i])) {
          axisLine = axisLines[i];
          break;
        }
      }

      if (!gridLine || !axisLine) return;
      const gridSpan = verticalLineSpanPx(gridLine);
      const axisSpan = verticalLineSpanPx(axisLine);
      if (gridSpan == null || axisSpan == null) return;

      const lengthDiff = gridSpan - axisSpan;
      if (Number.isFinite(lengthDiff) && Math.abs(lengthDiff) >= 0.5) {
        setGutterPlotMarginsY((prev) => {
          const half = lengthDiff / 2;
          const top = Math.min(
            PULSE_GUTTER_MARGIN_Y_MAX,
            Math.max(PULSE_GUTTER_MARGIN_Y_MIN, prev.top - half)
          );
          const bottom = Math.min(
            PULSE_GUTTER_MARGIN_Y_MAX,
            Math.max(PULSE_GUTTER_MARGIN_Y_MIN, prev.bottom - half)
          );
          if (Math.abs(top - prev.top) < 0.25 && Math.abs(bottom - prev.bottom) < 0.25) return prev;
          return { top, bottom };
        });
        return;
      }

      // Lengths match: align endpoints in viewport by shifting gutter vertically without changing total margin.
      const gTop = gridLine.getBoundingClientRect().top;
      const aTop = axisLine.getBoundingClientRect().top;
      const deltaTop = aTop - gTop;
      if (!Number.isFinite(deltaTop) || Math.abs(deltaTop) < 0.5) return;

      setGutterPlotMarginsY((prev) => {
        const nextTop = prev.top - deltaTop;
        const nextBottom = prev.bottom + deltaTop;
        const top = Math.min(PULSE_GUTTER_MARGIN_Y_MAX, Math.max(PULSE_GUTTER_MARGIN_Y_MIN, nextTop));
        const bottom = Math.min(PULSE_GUTTER_MARGIN_Y_MAX, Math.max(PULSE_GUTTER_MARGIN_Y_MIN, nextBottom));
        if (Math.abs(top - prev.top) < 0.25 && Math.abs(bottom - prev.bottom) < 0.25) return prev;
        return { top, bottom };
      });
    };

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(measureAndSync);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [needsScroll, pulseYAxisSyncLayoutKey, gutterPlotMarginsY.top, gutterPlotMarginsY.bottom]);

  // Handle price type selection
  const handlePriceTypeChange = (priceType) => {
    setSelectedPriceTypes((prev) =>
      prev.includes(priceType) ? prev.filter((p) => p !== priceType) : [...prev, priceType]
    );
  };

  // Handle location (terminal) selection
  const handleLocationChange = (location) => {
    setSelectedLocations((prev) => {
      const next = prev.includes(location) ? prev.filter((l) => l !== location) : [...prev, location];
      setLocationsUserCleared(next.length === 0);
      return next;
    });
  };

  // Handle origin selection
  const handleOriginChange = (origin) => {
    setSelectedOrigins((prev) => {
      const next = prev.includes(origin) ? prev.filter((o) => o !== origin) : [...prev, origin];
      setOriginsUserCleared(next.length === 0);
      return next;
    });
  };

  // Parse a lineKey ("Origin_low_price_Location" / "Origin_high_price_Location")
  // into { origin, priceTypeLabel, location }.
  const parseLineKey = (lineKey) => {
    const lowIdx = lineKey.lastIndexOf('_low_price_');
    const highIdx = lineKey.lastIndexOf('_high_price_');
    const splitLoc = (rest) => {
      const bits = String(rest).split('::');
      return { location: bits[0], extras: bits.slice(1) };
    };
    if (lowIdx !== -1) {
      const loc = splitLoc(lineKey.slice(lowIdx + 11));
      return { origin: lineKey.slice(0, lowIdx), priceTypeLabel: t('filters.lowPrice'), ...loc };
    }
    if (highIdx !== -1) {
      const loc = splitLoc(lineKey.slice(highIdx + 12));
      return { origin: lineKey.slice(0, highIdx), priceTypeLabel: t('filters.highPrice'), ...loc };
    }
    return null;
  };

  // Get display name for a line key. Drop the terminal location for a cleaner
  // label ("Mexico - High Price"); keep "… - {location}" only when another
  // active series shares the same origin + price type, so legend/tooltip stay
  // unambiguous. NOTE: the tooltip's terminal grouping (linePriceTooltipContent
  // above) keys off the raw dataKey, not this shortened name — keep it that way.
  const getLineDisplayName = (lineKey) => {
    const parts = parseLineKey(lineKey);
    if (!parts) return lineKey;
    const extra = (parts.extras || []).filter(Boolean).join(' - ');
    const extraBit = extra ? ` - ${extra}` : '';
    const base = `${parts.origin} - ${parts.priceTypeLabel}${extraBit}`;
    const collides = (lineKeys || []).some((k) => {
      if (k === lineKey) return false;
      const other = parseLineKey(k);
      if (!other) return false;
      const otherExtra = (other.extras || []).filter(Boolean).join(' - ');
      const otherBit = otherExtra ? ` - ${otherExtra}` : '';
      return `${other.origin} - ${other.priceTypeLabel}${otherBit}` === `${parts.origin} - ${parts.priceTypeLabel}${extraBit}`;
    });
    return collides ? `${base} - ${parts.location}` : base;
  };

  const helpDescription = "Daily terminal price trends showing low, high, and average prices over the selected period. Filter by type, variety, origin, and other attributes to analyze specific market segments.";

  // Single-chart fullscreen: below lg the overlay scrolls, so these boxes grow
  // with their content instead of being squeezed to 0px, and the plot keeps a
  // real height. See the `.preset-fullscreen-*` block in index.css.
  const fsFill = presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '';
  const fsPlot = presetSingleChartFullscreen ? ' preset-fullscreen-plot' : '';

  if (!data || data.length === 0) {
    return (
      <div className={(forceFiltersOpen ? 'w-full' : 'h-full') + ' flex flex-col bg-transparent'}>
        <PresetChartTitle
          forceFiltersOpen={forceFiltersOpen}
          title={t('charts.terminalPricePulse.title')}
          theme={theme}
          fontSize={fontSize}
          className={forceFiltersOpen ? 'px-4 pt-4' : 'border-y py-2 px-4'}
        />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.noDataText }}>
            {t('charts.terminalPricePulse.noData')}<br />{t('charts.selectAnotherTimeframe')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        (forceFiltersOpen
          ? presetSingleChartFullscreen
            ? 'w-full h-full min-h-0 flex-1' + fsFill
            : 'w-full'
          : 'h-full') + ' flex flex-col bg-transparent'
      }
    >
      <div className={`flex-1 flex flex-col min-h-0${fsFill} ${presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen)}`}>
        {/* Title */}
        <PresetChartTitle
          forceFiltersOpen={forceFiltersOpen}
          title={t('charts.terminalPricePulse.title')}
          theme={theme}
          fontSize={fontSize}
          right={
            <ChartFullscreenTitleControl
              analyticsFullscreen={analyticsFullscreen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
            />
          }
        />

        {/* Filters */}
        <div className="flex-shrink-0">
          {/* Filters Header — collapsible on small screens even in fullscreen
              (portrait panel); lg:hidden once forceFiltersOpen since desktop
              fullscreen keeps filters open via the CSS gate below. */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`w-full flex items-center justify-between py-2 border-b flex-shrink-0 ${forceFiltersOpen ? 'lg:hidden' : ''}`}
            style={{ borderColor: theme.borderSecondary }}
          >
            <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
            {filtersExpanded ? (
              <ChevronUp size={16} style={{ color: theme.secondaryText }} />
            ) : (
              <ChevronDown size={16} style={{ color: theme.secondaryText }} />
            )}
          </button>

          {(forceFiltersOpen || filtersExpanded) && (
            <div className={forceFiltersOpen ? (filtersExpanded ? 'max-lg:pt-3' : 'max-lg:hidden') : undefined}>
            <PresetFilterStrip
              forceFiltersOpen={forceFiltersOpen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
              analyticsFullscreen={analyticsFullscreen}
              titleHasFullscreenControl
              gridClassName="pt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 flex-shrink-0"
            >
            <FilterDropdown
              label={t('filters.findPrice')}
              value={selectedPriceTypes.length === 2
                ? t('filters.allPrices')
                : selectedPriceTypes.length === 1
                  ? (selectedPriceTypes[0] === 'low_price' ? t('filters.lowPrice') : t('filters.highPrice'))
                  : selectedPriceTypes.length > 0
                    ? t('filters.nSelected', { count: selectedPriceTypes.length })
                    : t('filters.selectPriceType')}
              onClick={() => setPriceModalOpen(true)}
              compact={forceFiltersOpen}
            />

            {priceUnitFieldsAvailable && (
              <FilterDropdown
                label={t('filters.priceUnit')}
                value={priceUnit === 'package' ? t('filters.package') : priceUnit === 'kg' ? t('filters.perKg') : t('filters.perLb')}
                onClick={() => setPriceUnitModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.origins.length > 0 && (
              <FilterDropdown
                label={t('filters.origin')}
                value={(() => {
                  const opts = availableOptions.origins;
                  const only = singleSelectionLabel(selectedOrigins, opts);
                  if (only) return only;
                  const activeCount = selectedOrigins.filter((orig) =>
                    opts.some((o) => String(o) === String(orig))
                  ).length;
                  if (opts.length > 0 && activeCount === opts.length) return t('filters.allOrigins');
                  if (activeCount > 0) return t('filters.nSelected', { count: activeCount });
                  if (selectedOrigins.length > 0) return t('filters.nSelected', { count: selectedOrigins.length });
                  return t('filters.selectOrigins');
                })()}
                onClick={() => setOriginModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.locations.length > 0 && (
              <FilterDropdown
                label={t('filters.terminal')}
                value={(() => {
                  const opts = availableOptions.locations;
                  const only = singleSelectionLabel(selectedLocations, opts);
                  if (only) return only;
                  const activeCount = selectedLocations.filter((loc) =>
                    opts.some((o) => String(o) === String(loc))
                  ).length;
                  if (opts.length > 0 && activeCount === opts.length) return t('filters.allTerminals');
                  if (activeCount > 0) return t('filters.nSelected', { count: activeCount });
                  if (selectedLocations.length > 0) return t('filters.nSelected', { count: selectedLocations.length });
                  return t('filters.selectTerminal');
                })()}
                onClick={() => setLocationModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.types.length > 0 && (
              <FilterDropdown
                label={t('filters.type')}
                value={selectedType || t('filters.selectType')}
                onClick={() => setTypeModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.varieties.length > 0 && (
              <FilterDropdown
                label={t('filters.variety')}
                value={multiSelectDropdownLabel(selectedVariety, availableOptions.varieties, {
                  allLabel: t('filters.allVarieties'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.selectVariety'),
                })}
                onClick={() => setVarietyModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {sizeRules && availableOptions.sizeGroups.length > 0 && (
              <FilterDropdown
                label={t('filters.sizeGroup', 'Size Group')}
                value={multiSelectDropdownLabel(selectedSizeGroup, availableOptions.sizeGroups, {
                  allLabel: t('filters.allSizeGroups', 'All Size Groups'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.noSizeGroup', 'No size group'),
                })}
                onClick={() => setSizeGroupModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {sizeRules && priceUnit === 'package' && availableOptions.packages.length > 0 && (
              <FilterDropdown
                label={t('filters.sku')}
                value={selectedPackage || t('filters.selectSku')}
                onClick={() => setPackageModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.sizes.length > 0 && (
              <FilterDropdown
                label={sizeRules ? t('filters.sizeGranularity', 'Size granularity') : t('filters.size')}
                value={multiSelectDropdownLabel(selectedSize, availableOptions.sizes, {
                  allLabel: sizeRules ? t('filters.allSizeGranularities', 'All size granularities') : t('filters.allSizes'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: sizeRules ? t('filters.noSizeGranularity', 'No size granularity') : t('filters.selectSize'),
                })}
                onClick={() => setSizeModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.grades.length > 0 && (
              <FilterDropdown
                label={t('filters.grade')}
                value={multiSelectDropdownLabel(selectedGrade, availableOptions.grades, {
                  allLabel: t('filters.allGrades'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.selectGrade'),
                })}
                onClick={() => setGradeModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {/* Only show SKU dropdown when price unit is 'package' (non-cucumber order: after grade) */}
            {!sizeRules && priceUnit === 'package' && availableOptions.packages.length > 0 && (
              <FilterDropdown
                label={t('filters.sku')}
                value={selectedPackage || t('filters.selectSku')}
                onClick={() => setPackageModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}

            {availableOptions.storages.length > 0 && (
              <FilterDropdown
                label={t('filters.storage')}
                value={multiSelectDropdownLabel(selectedStorage, availableOptions.storages, {
                  allLabel: t('filters.allStorage'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setStorageModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.conditions.length > 0 && (
              <FilterDropdown
                label="Condition"
                value={multiSelectDropdownLabel(selectedCondition, availableOptions.conditions, {
                  allLabel: t('filters.allCondition', 'All conditions'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setConditionModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.environments.length > 0 && (
              <FilterDropdown
                label="Environment"
                value={multiSelectDropdownLabel(selectedEnvironment, availableOptions.environments, {
                  allLabel: t('filters.allEnvironment', 'All environments'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setEnvironmentModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.qualities.length > 0 && (
              <FilterDropdown
                label={t('filters.quality')}
                value={multiSelectDropdownLabel(selectedQuality, availableOptions.qualities, {
                  allLabel: t('filters.allQuality'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                  getOptionLabel: (v) => getSelectedFieldValue(v),
                })}
                onClick={() => setQualityModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.appearances.length > 0 && (
              <FilterDropdown
                label="Appearance"
                value={multiSelectDropdownLabel(selectedAppearance, availableOptions.appearances, {
                  allLabel: t('filters.allAppearance', 'All appearances'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setAppearanceModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            {availableOptions.properties.length > 0 && (
              <FilterDropdown
                label={t('filters.properties', 'Properties')}
                value={multiSelectDropdownLabel(selectedProperties, availableOptions.properties, {
                  allLabel: t('filters.allProperties'),
                  nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                  emptyLabel: t('filters.select'),
                })}
                onClick={() => setPropertiesModalOpen(true)}
                fontSize={fontSize}
                compact={forceFiltersOpen}
              />
            )}
            </PresetFilterStrip>
            </div>
          )}
        </div>

        {/* Cards */}
      {forceFiltersOpen && !hidePresetScorecards ? (
        <div className="flex-shrink-0">
          <TerminalPricePulseScorecards
            apiParams={{ email, commodity, dateStart, dateEnd }}
            dims={{
              type: selectedType,
              variety: selectedVariety,
              origin: selectedOrigins,
              destination: selectedLocations,
              size: selectedSize,
              grade: selectedGrade,
              package: selectedPackage,
              storage: selectedStorage,
              condition: selectedCondition,
              environment: selectedEnvironment,
              quality: selectedQuality,
              appearance: selectedAppearance,
              properties: selectedProperties,
            }}
            gapDims={{
              type: selectedType,
              variety: selectedVariety,
              origin: selectedOrigins,
              size: selectedSize,
              grade: selectedGrade,
              package: selectedPackage,
              storage: selectedStorage,
              condition: selectedCondition,
              environment: selectedEnvironment,
              quality: selectedQuality,
              appearance: selectedAppearance,
              properties: selectedProperties,
            }}
          />
        </div>
      ) : presetScorecards ? (
        <div className="flex-shrink-0">{presetScorecards}</div>
      ) : null}

        {/* Chart */}
        <div
          className={
            (forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'flex-1 min-h-0' + fsFill) +
            ' flex flex-col overflow-visible bg-white dark:bg-[#191919] rounded-lg'
          }
        >
          {/* Fixed-height plot box (`preset-chart-plot`). The legend below is a
              sibling of this box, not a child: it is flex-shrink-0, so a
              many-series legend (one row per series on a phone) would consume
              the whole box and squeeze the flex-1 plot to 0px — the chart would
              silently vanish while its legend still rendered. */}
          <div
            className={
              (forceFiltersOpen && !presetSingleChartFullscreen
                ? 'preset-chart-plot'
                : 'flex-1' + fsPlot) + ' min-h-0 flex flex-col'
            }
          >
          {presetDescription ? (
            <div className="px-4 pt-4 pb-3 flex-shrink-0">
              {/* Filter summary only — the graph name lives in the card title above. */}
              <p className="text-[12px] text-[#828382] dark:text-gray-400 text-center">
                {(() => {
                  const commodityLabel = commodity
                    ? commodity
                        .split(' ')
                        .filter(Boolean)
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')
                    : 'this commodity';

                  const isMeaningful = (v) => v != null && v !== '' && v !== 'N/A' && v !== 'Not Classified';
                  const parts = [];

                  if (isMeaningful(selectedType)) parts.push(selectedType);
                  if (shouldNameSeriesForMulti(selectedVariety, availableOptions.varieties)) {
                    parts.push(selectedVariety.filter(isMeaningful).join(', '));
                  }
                  if (priceUnit) {
                    parts.push(priceUnit === 'package' ? t('filters.package') : priceUnit === 'kg' ? t('filters.perKg') : t('filters.perLb'));
                  }

                  const summary = parts.filter(Boolean).join(' · ');
                  return summary ? `${commodityLabel}, ${summary}` : commodityLabel;
                })()}
              </p>
            </div>
          ) : null}

          {/* Existing chart rendering continues below */}


        {/* Chart — when horizontally scrolled, Y-axis stays fixed in a left gutter */}
        <div className="flex-1 min-h-0 flex flex-row overflow-hidden">
          {chartData.length > 0 && lineKeys.length > 0 && needsScroll ? (
            <div
              className="relative flex-shrink-0 h-full"
              style={{
                width: scrollYAxisGutterPx,
                backgroundColor: theme.widgetBg,
              }}
            >
              {/* Hide y-axis label in preset view (matches design) */}
              {!forceFiltersOpen && (
                <span
                  className="pointer-events-none absolute z-[1] left-1 top-1/2 -translate-y-1/2 origin-center -rotate-90 whitespace-nowrap"
                  style={{ fontSize, color: theme.axisLabelFill }}
                >
                  {priceAxisLabelText}
                </span>
              )}
              <div ref={pulseYAxisChartRef} className="h-full w-full min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{
                      top: gutterPlotMarginsY.top,
                      right: 2,
                      left: compactYAxis
                        ? CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE
                        : CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN,
                      bottom: gutterPlotMarginsY.bottom,
                    }}
                  >
                    <XAxis
                      dataKey="sortKey"
                      type="number"
                      scale="time"
                      domain={xAxisConfig.domain}
                      ticks={xAxisConfig.ticks}
                      hide
                      height={60}
                      interval={0}
                      minTickGap={0}
                    />
                    <YAxis
                      width={priceYAxisWidth}
                      stroke={theme.axisStroke}
                      tick={{ fontSize, fill: theme.tickFill }}
                      tickCount={Y_AXIS_TICK_COUNT}
                      domain={getSmartYDomain(chartData, lineKeys)}
                      allowDataOverflow={false}
                      tickFormatter={formatPriceTick}
                    />
                    <Legend
                      verticalAlign="bottom"
                      wrapperStyle={{
                        fontSize,
                        paddingTop: 20,
                        height: LEGEND_HEIGHT_PX,
                        overflow: 'hidden',
                        visibility: 'hidden',
                        // Marker consumed by the mobile legend-wrap CSS rule (B4) so it can
                        // exclude this hidden axis-sync gutter legend — its fixed height must
                        // survive at every breakpoint or the gutter's Y axis drifts from the
                        // main chart's. See index.css `.recharts-legend-wrapper` mobile rule.
                        '--chart-legend-gutter': 1,
                      }}
                    />
                    {lineKeys[0] ? (
                      <Line
                        key="y-axis-proxy"
                        type="monotone"
                        dataKey={lineKeys[0]}
                        stroke="transparent"
                        strokeWidth={0}
                        dot={false}
                        activeDot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ) : null}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          <div className="flex-1 min-w-0 min-h-0 flex flex-col" ref={plotAreaRef}>
            {chartData.length > 0 && lineKeys.length > 0 ? (
              <div
                className={fitXToWidth ? 'h-full min-h-0 overflow-hidden' : 'chart-x-scroll h-full min-h-0 overflow-x-auto overflow-y-hidden'}
                ref={scrollViewportRef}
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#666 transparent',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div
                  style={{
                    width: needsScroll ? chartWidth : '100%',
                    minWidth: needsScroll ? effectiveMinWidth : undefined,
                    height: '100%',
                  }}
                >
                  <div
                    ref={needsScroll ? pulseGridChartRef : undefined}
                    className="h-full w-full min-h-0"
                    onPointerDown={markTapStart}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={getResponsiveChartMargin({ isBelowLg, isPhone: compactYAxis, needsScroll })}
                        onClick={pinTooltip}
                        onTouchEnd={pinTooltip}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} vertical horizontal />
                        <XAxis
                          dataKey="sortKey"
                          type="number"
                          scale="time"
                          domain={xAxisConfig.domain}
                          ticks={fitXToWidth ? undefined : xAxisConfig.ticks}
                          stroke={theme.axisStroke}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          interval={fitXToWidth ? 'preserveStartEnd' : 0}
                          minTickGap={fitXToWidth ? 8 : 0}
                          tick={{ fontSize, fill: theme.tickFill }}
                          tickFormatter={(v) => {
                            if (v == null) return '';
                            const d = new Date(Number(v));
                            if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
                            return formatDateShort(d);
                          }}
                        />
                        {needsScroll ? (
                          <YAxis
                            hide
                            width={0}
                            tick={{ fontSize, fill: theme.tickFill }}
                            domain={getSmartYDomain(chartData, lineKeys)}
                            tickCount={Y_AXIS_TICK_COUNT}
                            allowDataOverflow={false}
                          />
                        ) : (
                          <YAxis
                            width={priceYAxisWidth}
                            stroke={theme.axisStroke}
                            tick={{ fontSize, fill: theme.tickFill }}
                            tickCount={Y_AXIS_TICK_COUNT}
                            domain={getSmartYDomain(chartData, lineKeys)}
                            allowDataOverflow={false}
                            tickFormatter={formatPriceTick}
                            label={
                              forceFiltersOpen
                                ? undefined
                                : {
                                    value: priceAxisLabelText,
                                    angle: -90,
                                    position: 'insideLeft',
                                    dx: -12,
                                    fill: theme.axisLabelFill,
                                    fontSize,
                                  }
                            }
                          />
                        )}
                        <Tooltip
                          key={tooltipDismissKey}
                          trigger={tooltipTrigger}
                          active={tooltipActive}
                          position={tooltipPosition}
                          isAnimationActive={false}
                          throttleDelay={50}
                          content={linePriceTooltipContent}
                          wrapperStyle={{ pointerEvents: 'auto', maxWidth: 'min(92vw, 28rem)' }}
                          cursor={tooltipCursorX == null}
                        />
                        {tooltipCursorX != null ? (
                          <ReferenceLine x={tooltipCursorX} stroke={theme.tickFill} strokeWidth={1} strokeDasharray="3 3" ifOverflow="extendDomain" />
                        ) : null}
                        {tooltipCursorX != null
                          ? (tooltipPinPayload || []).map((entry) => {
                              if (entry == null || entry.value == null || Number.isNaN(Number(entry.value))) return null;
                              return (
                                <ReferenceDot
                                  key={String(entry.dataKey)}
                                  x={tooltipCursorX}
                                  y={entry.value}
                                  r={4}
                                  fill={entry.color}
                                  stroke={entry.color}
                                  ifOverflow="extendDomain"
                                />
                              );
                            })
                          : null}
                        {!hideLegend && (
                          <Legend
                            verticalAlign="bottom"
                            wrapperStyle={{
                              fontSize,
                              paddingTop: 20,
                              height: LEGEND_HEIGHT_PX,
                              overflow: 'hidden',
                            }}
                          />
                        )}
                        {lineKeys.map((lineKey, index) => (
                          <Line
                            key={lineKey}
                            type="monotone"
                            dataKey={lineKey}
                            name={getLineDisplayName(lineKey)}
                            stroke={pulseLineColor(lineKey)}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                            connectNulls
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm" style={{ color: theme.secondaryText }}>
                  {selectedOrigins.length === 0
                    ? t('charts.selectAtLeastOneOrigin')
                    : selectedLocations.length === 0 || selectedPriceTypes.length === 0
                      ? t('charts.selectAtLeastOneRegion')
                      : t('charts.noMatchingFilters')}
                </p>
              </div>
            )}
          </div>
        </div>
          </div>
        {/* Legend. Fullscreen moved up beside the filter strip. */}
        {presetDescription && hideLegend && chartData.length > 0 && lineKeys.length > 0 ? (
          <PresetChartLegend theme={theme} fullscreen={presetSingleChartFullscreen}>
            {lineKeys.map((lineKey, index) => {
              const color = pulseLineColor(lineKey);
              return (
                <div key={lineKey} className="flex min-w-0 items-start gap-2 max-lg:w-full lg:shrink-0" role="listitem">
                  <svg width={24} height={10} className="shrink-0 block" aria-hidden>
                    <line
                      x1={2}
                      y1={5}
                      x2={22}
                      y2={5}
                      stroke={color}
                      strokeWidth={2}
                      strokeLinecap="round"
                    />
                  </svg>
                  <span
                    className="min-w-0 whitespace-normal break-words text-left leading-snug"
                    style={{ fontSize: Math.max(fontSize - 1, 10), color: theme.tickFill }}
                  >
                    {getLineDisplayName(lineKey)}
                  </span>
                </div>
              );
            })}
          </PresetChartLegend>
        ) : null}
        {seriesCapped ? (
          <p className={`flex-shrink-0 px-4 text-xs ${forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'pb-2'}`} style={{ color: theme.secondaryText }}>
            Showing {lineKeys.length} of {seriesTotal} series. Narrow Origin, Size, or Properties to see the rest.
          </p>
        ) : null}

      </div>

      {/* Modals */}
      <FilterModal
        isOpen={priceModalOpen}
        onClose={() => setPriceModalOpen(false)}
        title={t('filters.selectPriceType')}
        options={['low_price', 'high_price']}
        selectedValues={selectedPriceTypes}
        onSelect={handlePriceTypeChange}
        multiSelect
        onSelectAll={() => setSelectedPriceTypes(['low_price', 'high_price'])}
        onClearAll={() => setSelectedPriceTypes([])}
        displayFormatter={(option) => option === 'low_price' ? t('filters.lowPrice') : t('filters.highPrice')}
      />

      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title={t('filters.selectType')}
        options={availableOptions.types.length > 0 ? availableOptions.types : ['Conventional', 'Organic']}
        selectedValues={selectedType}
        onSelect={(value) => {
          setSelectedType(value);
          cascadeBelow('type', { type: value });
        }}
      />

      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title={t('filters.selectVariety')}
        options={availableOptions.varieties}
        selectedValues={selectedVariety}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedVariety, VARIETY_SELECT_MAX);
          const fallback = defaultPulseVarieties(data);
          const val = next.length ? next : fallback;
          setSelectedVariety(val);
          cascadeBelow('variety', { variety: val });
        }}
        multiSelect
        onSelectAll={() => {
          const val = defaultPulseVarieties(data);
          setSelectedVariety(val);
          cascadeBelow('variety', { variety: val });
        }}
        onClearAll={() => {
          const val = defaultPulseVarieties(data);
          setSelectedVariety(val);
          cascadeBelow('variety', { variety: val });
        }}
      />

      <FilterModal
        isOpen={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          cascadeBelow(null);
        }}
        title={t('filters.selectTerminal')}
        options={availableOptions.locations}
        selectedValues={selectedLocations}
        onSelect={handleLocationChange}
        multiSelect
        onSelectAll={() => {
          const rows = applyGeographicDataFilter(data, selectedOrigins, []);
          setSelectedLocations(getCleanOptions(rows, 'location'));
          setLocationsUserCleared(false);
        }}
        onClearAll={() => {
          setSelectedLocations([]);
          setLocationsUserCleared(true);
        }}
      />

      <FilterModal
        isOpen={originModalOpen}
        onClose={() => {
          setOriginModalOpen(false);
          cascadeBelow(null);
        }}
        title={t('filters.selectOrigins')}
        options={availableOptions.origins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginChange}
        multiSelect
        onSelectAll={() => {
          setSelectedOrigins(getCleanOptions(data, 'origin'));
          setOriginsUserCleared(false);
        }}
        onClearAll={() => {
          setSelectedOrigins([]);
          setOriginsUserCleared(true);
        }}
      />

      {sizeRules && availableOptions.sizeGroups.length > 0 && (
      <FilterModal
        isOpen={sizeGroupModalOpen}
        onClose={() => setSizeGroupModalOpen(false)}
        title={t('filters.sizeGroup', 'Size Group')}
        options={availableOptions.sizeGroups}
        selectedValues={selectedSizeGroup}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSizeGroup);
          setSelectedSizeGroup(next);
          cascadeBelow('sizeGroup', { sizeGroup: next });
        }}
        multiSelect
        onSelectAll={() => {
          setSelectedSizeGroup(availableOptions.sizeGroups);
          cascadeBelow('sizeGroup', { sizeGroup: availableOptions.sizeGroups });
        }}
        onClearAll={() => {
          setSelectedSizeGroup([]);
          cascadeBelow('sizeGroup', { sizeGroup: [] });
        }}
      />
      )}

      <FilterModal
        isOpen={sizeModalOpen && availableOptions.sizes.length > 0}
        onClose={() => setSizeModalOpen(false)}
        title={sizeRules ? t('filters.sizeGranularity', 'Size granularity') : t('filters.selectSize')}
        options={availableOptions.sizes}
        selectedValues={selectedSize}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSize);
          const val = sizeRules ? next : (next.length ? next : availableOptions.sizes);
          setSelectedSize(val);
          cascadeBelow('size', { size: val });
        }}
        multiSelect
        onSelectAll={() => {
          setSelectedSize(availableOptions.sizes);
          cascadeBelow('size', { size: availableOptions.sizes });
        }}
        onClearAll={() => {
          if (sizeRules) {
            setSelectedSize([]);
            cascadeBelow('size', { size: [] });
            return;
          }
          setSelectedSize(availableOptions.sizes);
          cascadeBelow('size', { size: availableOptions.sizes });
        }}
      />

      <FilterModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        title={t('filters.selectGrade')}
        options={availableOptions.grades}
        selectedValues={selectedGrade}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedGrade);
          setSelectedGrade(next.length ? next : availableOptions.grades);
          cascadeBelow('grade', { grade: next.length ? next : availableOptions.grades });
        }}
        multiSelect
        onSelectAll={() => {
          setSelectedGrade(availableOptions.grades);
          cascadeBelow('grade', { grade: availableOptions.grades });
        }}
        onClearAll={() => {
          setSelectedGrade(availableOptions.grades);
          cascadeBelow('grade', { grade: availableOptions.grades });
        }}
      />

      <FilterModal
        isOpen={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        title={t('filters.selectSku')}
        options={availableOptions.packages}
        selectedValues={selectedPackage}
        onSelect={(value) => {
          setSelectedPackage(value);
          cascadeBelow('package', { package: value });
        }}
      />

      <FilterModal
        isOpen={priceUnitModalOpen}
        onClose={() => setPriceUnitModalOpen(false)}
        title={t('filters.priceUnit')}
        options={['package', 'kg', 'lbs']}
        selectedValues={priceUnit}
        onSelect={(value) => {
          if (value === priceUnit) return;
          setPriceUnit(value);
          if (value === 'package') {
            restorePackageSelection();
          } else {
            if (selectedPackage) lastPackageRef.current = selectedPackage;
            setSelectedPackage('');
          }
        }}
        displayFormatter={(option) => option === 'package' ? t('filters.package') : option === 'kg' ? t('filters.perKg') : t('filters.perLb')}
      />

      <FilterModal isOpen={storageModalOpen} onClose={() => setStorageModalOpen(false)} title="Select Storage"
        options={availableOptions.storages} selectedValues={selectedStorage}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedStorage);
          const val = next.length ? next : availableOptions.storages;
          setSelectedStorage(val);
          cascadeBelow('storage', { storage: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedStorage(availableOptions.storages); cascadeBelow('storage', { storage: availableOptions.storages }); }}
        onClearAll={() => { setSelectedStorage(availableOptions.storages); cascadeBelow('storage', { storage: availableOptions.storages }); }}
      />
      <FilterModal isOpen={conditionModalOpen} onClose={() => setConditionModalOpen(false)} title="Select Condition"
        options={availableOptions.conditions} selectedValues={selectedCondition}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedCondition);
          const val = next.length ? next : availableOptions.conditions;
          setSelectedCondition(val);
          cascadeBelow('condition', { condition: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedCondition(availableOptions.conditions); cascadeBelow('condition', { condition: availableOptions.conditions }); }}
        onClearAll={() => { setSelectedCondition(availableOptions.conditions); cascadeBelow('condition', { condition: availableOptions.conditions }); }}
      />
      <FilterModal isOpen={environmentModalOpen} onClose={() => setEnvironmentModalOpen(false)} title="Select Environment"
        options={availableOptions.environments} selectedValues={selectedEnvironment}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedEnvironment);
          const val = next.length ? next : availableOptions.environments;
          setSelectedEnvironment(val);
          cascadeBelow('environment', { environment: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedEnvironment(availableOptions.environments); cascadeBelow('environment', { environment: availableOptions.environments }); }}
        onClearAll={() => { setSelectedEnvironment(availableOptions.environments); cascadeBelow('environment', { environment: availableOptions.environments }); }}
      />
      <FilterModal
        isOpen={qualityModalOpen}
        onClose={() => setQualityModalOpen(false)}
        title="Select Quality"
        options={availableOptions.qualities}
        selectedValues={selectedQuality}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedQuality);
          const val = next.length ? next : availableOptions.qualities;
          setSelectedQuality(val);
          cascadeBelow('quality', { quality: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedQuality(availableOptions.qualities); cascadeBelow('quality', { quality: availableOptions.qualities }); }}
        onClearAll={() => { setSelectedQuality(availableOptions.qualities); cascadeBelow('quality', { quality: availableOptions.qualities }); }}
        displayFormatter={(value) => getSelectedFieldValue(value)}
      />
      <FilterModal isOpen={appearanceModalOpen} onClose={() => setAppearanceModalOpen(false)} title="Select Appearance"
        options={availableOptions.appearances} selectedValues={selectedAppearance}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedAppearance);
          const val = next.length ? next : availableOptions.appearances;
          setSelectedAppearance(val);
          cascadeBelow('appearance', { appearance: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedAppearance(availableOptions.appearances); cascadeBelow('appearance', { appearance: availableOptions.appearances }); }}
        onClearAll={() => { setSelectedAppearance(availableOptions.appearances); cascadeBelow('appearance', { appearance: availableOptions.appearances }); }}
      />
      <FilterModal isOpen={propertiesModalOpen} onClose={() => setPropertiesModalOpen(false)} title="Select Properties"
        options={availableOptions.properties} selectedValues={selectedProperties}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedProperties);
          const val = next.length ? next : availableOptions.properties;
          setSelectedProperties(val);
          cascadeBelow('properties', { properties: val });
        }}
        multiSelect
        onSelectAll={() => { setSelectedProperties(availableOptions.properties); cascadeBelow('properties', { properties: availableOptions.properties }); }}
        onClearAll={() => { setSelectedProperties(availableOptions.properties); cascadeBelow('properties', { properties: availableOptions.properties }); }}
      />
    </div>
    </div>
  );
};

export default TerminalPriceLineChart;
